// NOTE: Adding @sentry/react to the dependencies causes an error because of dependencies it installs.
// Our other sentry dependencies (@sentry/react-native) already install @sentry/react so we can ignore this error.
// eslint-disable-next-line import/no-extraneous-dependencies
import * as Sentry from "@sentry/react";
import Matercolor from "matercolors";
import type { ITheme } from "native-base";
import { extendTheme, theme as defaultTheme } from "native-base";

import { FeatureFlag, isFeatureFlagActive } from "../helpers/featureFlags";
import { doesTheUserHaveMenstrualPlanSet, getOrganisation } from "../helpers/userHelpers";
import type { Brand, User } from "../services/backendTypes";
import type { OnboardingState } from "../slices/onboardingSlice";

// Add TypeScript type declaration for custom theme properties
declare module "native-base" {
  interface ICustomTheme {
    colors: {
      primary: Record<string | number, string>;
      secondary: Record<string | number, string>;
      tertiary: Record<string | number, string>;
      gray: Record<string | number, string>;
      coolGray: Record<string | number, string>;
      error: Record<string | number, string>;
      warning: Record<string | number, string>;
      success: Record<string | number, string>;
      amber: Record<string | number, string>;
      muted: Record<string | number, string>;
      function: {
        10: string;
        20: string;
        30: string;
        40: string;
        50: string;
      };
    } & Record<string, Record<string | number, string> | string>;
    borderRadii: {
      none: number;
      xs: number;
      sm: number;
      md: number;
      lg: number;
      xl: number;
      "2xl": number;
      "3xl": number;
      full: number;
      card: {
        default: number;
        small: number;
        large: number;
      };
      button: {
        default: number;
        small: number;
        large: number;
        pill: number;
      };
      input: {
        default: number;
      };
      modal: {
        default: number;
        sheet: number;
      };
      badge: {
        default: number;
        pill: number;
      };
    };
    fonts: {
      heading: string;
      body: string;
      mono: string;
      serif: string;
    };
  }
}

// Design tokens based on System UI Theme Specification
const APP_DEFAULT_PRIMARY_COLOUR = "#D8EB95";
const APP_SECONDARY_COLOUR = "#A7D67F";
const APP_ACCENT_COLOUR = "#C7F3E2";

// Override variable to force use of default theme
const USE_DEFAULT_THEME = false;

// Override variable to use Food with Ferne theme
// This can be controlled via the EXPO_PUBLIC_USE_FOOD_WITH_FERNE_THEME environment variable
const USE_FOOD_WITH_FERNE_THEME = process.env.EXPO_PUBLIC_USE_FOOD_WITH_FERNE_THEME === "true";

// Override variable to use My Nguyen theme
// This can be controlled via the EXPO_PUBLIC_USE_MY_NGUEN_THEME environment variable
const USE_MY_NGUEN_THEME = process.env.EXPO_PUBLIC_USE_MY_NGUEN_THEME === "true";

export function createThemeFromColour(colour: string | undefined): ITheme {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const appDefaultPalette = new Matercolor(APP_DEFAULT_PRIMARY_COLOUR);

  let palette = appDefaultPalette;

  // If USE_DEFAULT_THEME is true, always use default colors regardless of custom branding
  if (USE_DEFAULT_THEME) {
    palette = appDefaultPalette;
  } else if (colour) {
    // If the user's coach (or the coach themselves) has custom branding, use that instead of the default
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      palette = new Matercolor(colour);
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  // NOTE: We adjust it because this becomes primary.400, but NativeBase default is 600. So we lighten it a bit.
  // We move all values above 200 down by 2 places if the primary colour is not black

  let primaryColourPalette = {
    ...palette,
  };

  if (colour === "#000000") {
    // Special handling for black primary color to ensure proper contrast
    // Keep light shades for backgrounds but use dark for selected states
    primaryColourPalette = {
      ...palette,
      10: palette[50], // Keep light for backgrounds
      20: palette[200], // Light-medium
      30: palette[300], // Medium
      40: palette[500], // Medium-dark
      50: palette[800], // Use dark (not black) for selected states to ensure contrast
    };
  } else if (colour !== "#000000") {
    primaryColourPalette = {
      ...palette,
      10: palette[50],
      20: palette[200],
      30: palette[300],
      40: palette[500],
      50: palette[700],
    };
  }

  // Create secondary color palette from design tokens
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const secondaryPalette = new Matercolor(APP_SECONDARY_COLOUR);

  // Create accent color palette from design tokens
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const accentPalette = new Matercolor(APP_ACCENT_COLOUR);

  const theme: ITheme = {
    ...defaultTheme,
    colors: {
      ...defaultTheme.colors,
      primary: primaryColourPalette,
      secondary: secondaryPalette,
      tertiary: accentPalette,
      // Add new function colors for text, states, and UI elements
      function: {
        10: "#faf7f3",
        20: "#c4bcb8",
        30: "#928882",
        40: "#5f534b",
        50: "#2c1e14",
      },
    },
    fontConfig: {
      DMSans: {
        400: {
          normal: "DMSans_400Regular",
        },
        500: {
          normal: "DMSans_500Medium",
        },
        700: {
          normal: "DMSans_700Bold",
        },
      },
      PlayfairDisplay: {
        400: {
          normal: "PlayfairDisplay_400Regular",
        },
        700: {
          normal: "PlayfairDisplay_700Bold",
        },
        900: {
          normal: "PlayfairDisplay_900Black",
        },
      },
      Inter: {
        400: {
          normal: "Inter_400Regular",
        },
        500: {
          normal: "Inter_500Medium",
        },
        600: {
          normal: "Inter_600SemiBold",
        },
        700: {
          normal: "Inter_700Bold",
        },
      },
      HelveticaNeue: {
        400: {
          normal: "HelveticaNeue",
        },
        500: {
          normal: "HelveticaNeue-Medium",
        },
        600: {
          normal: "HelveticaNeue-Bold",
        },
        700: {
          normal: "HelveticaNeue-Bold",
        },
      },
    },
    fonts: {
      heading: "HelveticaNeue-Bold",
      body: "Inter_500Medium",
      mono: "Inter_400Regular",
      serif: "HelveticaNeue-Bold",
    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any -- NativeBase font type definitions are missing
    // Add custom border radius system
    borderRadii: {
      // Base values
      none: 0,
      xs: 4,
      sm: 6,
      md: 8,
      lg: 12,
      xl: 16,
      "2xl": 20,
      "3xl": 24,
      full: 9999,

      // Semantic values for specific components
      card: {
        default: 12,
        small: 10,
        large: 16,
      },
      button: {
        default: 8,
        small: 6,
        large: 10,
        pill: 20,
      },
      input: {
        default: 8,
      },
      modal: {
        default: 16,
        sheet: 24,
      },
      badge: {
        default: 6,
        pill: 12,
      },
    },
    shadows: {
      ...defaultTheme.shadows,
      10: {
        shadowColor: "#000",
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 4,
      },
    },
    config: {
      initialColorMode: "light",
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- NativeBase types don't support custom properties
  } as any;

  return extendTheme(theme);
}

export function createMyNguyenTheme(): ITheme {
  // Create the My Nguyen color palette based on Figma design
  const myNguyenThemePalette = {
    // Theme colors based on Figma pink/burgundy palette
    10: "#fdf9f5", // Light cream - main background (from Figma background)
    20: "#f5d6d6", // Light pink
    30: "#d4a5a5", // Medium pink
    40: "#b85450", // Darker burgundy - selected states
    50: "#8b3a37", // Darkest burgundy
  };

  const theme: ITheme = {
    ...defaultTheme,
    colors: {
      ...defaultTheme.colors,
      // Use My Nguyen theme colors for primary
      primary: myNguyenThemePalette,
      // Keep secondary and tertiary consistent with primary
      secondary: myNguyenThemePalette,
      tertiary: myNguyenThemePalette,
      // Function colors for text and UI states from Figma grayscale
      function: {
        10: "#f5f5f5", // Lightest gray - off-white
        20: "#b0b0b0", // Light gray - disabled states, borders
        30: "#808080", // Medium gray - secondary text
        40: "#404040", // Dark gray - primary text
        50: "#1a1a1a", // Darkest - headings, emphasis
      },
    },
    fontConfig: {
      ...defaultTheme.fontConfig,
      // My Nguyen theme fonts based on Figma design
      PlayfairDisplay: {
        400: {
          normal: "PlayfairDisplay_400Regular",
        },
        700: {
          normal: "PlayfairDisplay_700Bold",
        },
      },
      Inter: {
        400: {
          normal: "Inter_400Regular",
        },
        500: {
          normal: "Inter_500Medium",
        },
        600: {
          normal: "Inter_600SemiBold",
        },
        700: {
          normal: "Inter_700Bold",
        },
      },
    },
    fonts: {
      heading: "PlayfairDisplay_700Bold", // For H1, H2 headings based on Figma design
      body: "Inter_400Regular", // For body text, tags
      mono: "Inter_400Regular", // Mono font (keeping consistent)
      serif: "PlayfairDisplay_400Regular", // Alternative serif option
    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any -- NativeBase font type definitions are missing
    // Border radius system based on Figma design tokens
    borderRadii: {
      // Base values from Figma design system
      none: 0,
      xs: 4, // Date picker selections
      sm: 8, // Cards and images
      md: 12,
      lg: 16,
      xl: 24,
      "2xl": 40,
      "3xl": 40,
      full: 9999, // Fully rounded elements like star icons

      // Semantic values for specific components
      card: {
        default: 8, // Matching meal cards in design
        small: 8,
        large: 12,
      },
      button: {
        default: 8,
        small: 4,
        large: 12,
        pill: 9999, // For fully rounded buttons like star icons
      },
      input: {
        default: 8,
      },
      modal: {
        default: 16,
        sheet: 24,
      },
      badge: {
        default: 8,
        pill: 9999,
      },
    },
    shadows: {
      ...defaultTheme.shadows,
      // Card shadows for My Nguyen theme based on Figma design
      card: {
        shadowColor: "#a48080", // Pinkish-brown shadow that matches the theme
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.35,
        shadowRadius: 19,
        elevation: 8,
      },
      // Additional shadow variations for different UI elements
      10: {
        shadowColor: "#a48080",
        shadowOffset: {
          width: 0,
          height: 2,
        },
        shadowOpacity: 0.2,
        shadowRadius: 8,
        elevation: 3,
      },
      20: {
        shadowColor: "#a48080",
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.35,
        shadowRadius: 19,
        elevation: 8,
      },
    },
    config: {
      initialColorMode: "light",
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- NativeBase types don't support custom properties
  } as any;

  return extendTheme(theme);
}

export function createFoodWithFerneTheme(): ITheme {
  // Create the Food with Ferne color palette based on Figma design
  const ferneThemePalette = {
    // Theme colors for backgrounds and decorative elements
    10: "#faf7f3", // Lightest cream - main background
    20: "#d3c6c0", // Light rose
    30: "#ac968c", // Medium brown
    40: "#866559", // Darker brown - selected states
    50: "#5f3426", // Darkest brown
  };

  const theme: ITheme = {
    ...defaultTheme,
    colors: {
      ...defaultTheme.colors,
      // Use Ferne theme colors for primary
      primary: ferneThemePalette,
      // Keep secondary and tertiary consistent with primary
      secondary: ferneThemePalette,
      tertiary: ferneThemePalette,
      // Function colors for text and UI states (these already exist in base theme)
      function: {
        10: "#f7f3ef", // Lightest - off-white
        20: "#c4bcb8", // Light gray - disabled states, borders
        30: "#928882", // Medium gray - secondary text
        40: "#5f534b", // Dark gray - primary text
        50: "#2c1e14", // Darkest - headings, emphasis
      },
    },
    fontConfig: {
      ...defaultTheme.fontConfig,
      // Add font configurations that match Figma design
      InstrumentSerif: {
        400: {
          normal: "InstrumentSerif_400Regular",
          italic: "InstrumentSerif_400Regular_Italic",
        },
      },
      Poppins: {
        400: {
          normal: "Poppins_400Regular",
        },
        500: {
          normal: "Poppins_500Medium",
        },
        600: {
          normal: "Poppins_600SemiBold",
        },
        700: {
          normal: "Poppins_700Bold",
        },
      },
    },
    fonts: {
      heading: "Poppins_500Medium",
      body: "Poppins_400Regular",
      mono: "Poppins_400Regular",
      serif: "InstrumentSerif_400Regular",
    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any -- NativeBase font type definitions are missing
    // Food with Ferne theme uses 90-degree angles (no border radius)
    borderRadii: {
      // Base values - all set to 0 for sharp corners
      none: 0,
      xs: 0,
      sm: 0,
      md: 0,
      lg: 0,
      xl: 0,
      "2xl": 0,
      "3xl": 0,
      full: 9999, // Keep this for circular elements if needed

      // Semantic values for specific components - all 0 for sharp corners
      card: {
        default: 0,
        small: 0,
        large: 0,
      },
      button: {
        default: 0,
        small: 0,
        large: 0,
        pill: 0,
      },
      input: {
        default: 0,
      },
      modal: {
        default: 0,
        sheet: 0,
      },
      badge: {
        default: 0,
        pill: 0,
      },
    },
    shadows: {
      ...defaultTheme.shadows,
    },
    config: {
      initialColorMode: "light",
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- NativeBase types don't support custom properties
  } as any;

  return extendTheme(theme);
}

export function shouldWeChangeAppToHormoneTheme(user: User | null, onboardingData: OnboardingState): boolean {
  if (!user || !isFeatureFlagActive(user, FeatureFlag.FF_FEMALE_HORMONES)) {
    return false;
  }

  const isOnHormoneDiet = ["MENOPAUSE", "CYCLE_DIET"].includes(onboardingData?.dietPreferences?.dietType);

  const isOnCycleDiet = Boolean(doesTheUserHaveMenstrualPlanSet(user));

  const isOnMenopauseDiet = Boolean(user.intake?.is_on_menopause_diet);

  return isOnHormoneDiet || isOnCycleDiet || isOnMenopauseDiet;
}

export const getTheme = (
  user: User | null,
  brandIfNotLoggedIn: Brand | undefined,
  onboardingData: OnboardingState
): ITheme => {
  // Developer override for My Nguyen theme
  if (USE_MY_NGUEN_THEME) {
    return createMyNguyenTheme();
  }

  // Developer override for Food with Ferne theme
  if (USE_FOOD_WITH_FERNE_THEME) {
    return createFoodWithFerneTheme();
  }

  let primaryColour;
  if (user) {
    const organisation = getOrganisation(user);
    primaryColour = organisation?.primary_colour || undefined;

    // FIXME: Also change the icon

    if (shouldWeChangeAppToHormoneTheme(user, onboardingData)) {
      primaryColour = HORMONE_HEX_COLOR;
    }
  } else {
    // Not logged in
    primaryColour = brandIfNotLoggedIn?.primary_colour || undefined;
  }

  return createThemeFromColour(primaryColour);
};

export const HORMONE_HEX_COLOR = "#AE79AB";

// Old colours for reference
// enum Colours {
//   primaryGreenColor = "#58CA8C",
//   secondaryGreenColor = "#8ADAAF",
//   whiteColor = "#FFFFFF",
//   largeTextColor = "#28414D",
//   disableButton = "#E2EBF0",
//   textColor = "#718A96",
//   bgColor = "#ffffff",
//   disableColor = "#A1B5C0",
//   grey = "#F7FAFC",
//   greyTextColor = "#718A96",
//   darkGreyTextColor = "#78909C",
//   sectionTextColor = "#4A5E68",
//   lightBrownColor = "#E1BD97",
//   darkgrey2 = "#A0B5C0",
//   white = "#FFF",
//   bgEmerald = "#8ADAAF",
//   bgPurple = "#9C9BDF",
//   bgBlizzardBlue = "#8AD1DA",
//   bgTangerine = "#E9BB91",
//   borderColor = "#CBD9E0",
//   descriptionTextColor = "#092939",
//   blueTextColor = "#5896CA",
//   smallTextColor = "#A0B5C0",
//   lightGreen = "#F3FBF7",
//   swiperColor = "#105A49",
//   grayBorder = "#EDF4F7",
//   tipColor = "#58ca8c1a",
//   blueColor = "#2D44A0",
//   pink = "#CD8DA5",
//   greyBgColor = "#E2E9F0",
// }
